"use client";

import React from "react";
import { MapPin, Navigation, ShieldCheck, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const locations = [{
  name: "North Fort Myers",
  description: "Our Headquarters & Main Base",
  isBase: true
}, {
  name: "Cape Coral",
  description: "Comprehensive Daily Coverage",
  isBase: false
}, {
  name: "Punta Gorda",
  description: "Dedicated Service Routes",
  isBase: false
}, {
  name: "Port Charlotte",
  description: "Extended Service Area",
  isBase: false
}];
export default function NorthFortMyersServiceArea() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="north-fort-myers-service-area" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          <div ref={textRef} className={`flex flex-col gap-6 transition-all duration-700 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-4">
              <Badge variant="secondary" className="px-3 py-1 text-sm">
                <MapPin className="mr-1 size-4" />
                Local Coverage
              </Badge>
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                Our Primary Service Area: <span className="text-primary">North Fort Myers</span>
              </h2>
              <p className="text-muted-foreground text-lg font-sans max-w-xl">
                While North Fort Myers is our core service area, we proudly extend coverage to nearby communities.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {locations.map((loc, i) => <Card key={loc.name} data-index={i} className={`overflow-hidden transition-colors ${loc.isBase ? "bg-primary text-primary-foreground border-primary shadow-md" : "bg-card text-card-foreground border-border hover:border-primary/50"}`}>
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className={`mt-0.5 shrink-0 rounded-full p-2 ${loc.isBase ? "bg-background/20" : "bg-muted"}`}>
                      {loc.isBase ? <ShieldCheck className="size-5" /> : <Navigation className="size-5 text-muted-foreground" />}
                    </div>
                    <div>
                      <h3 className="font-default font-semibold text-lg leading-none mb-1.5">
                        {loc.name}
                      </h3>
                      <p className={`text-sm font-sans ${loc.isBase ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                        {loc.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>

            <div className="mt-6">
              <Button asChild size="lg" className="font-sans font-semibold">
                <Link to="/ContactPage">
                  Check Availability in Your Area
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div ref={visualRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isVisualVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-background">
              <AspectRatio ratio={4 / 3}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/north_fort_myers_area_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/north_fort_myers_area_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/north_fort_myers_area_map.png-wvc-sizes" alt="Map showing North Fort Myers and surrounding service areas" className="object-cover w-full h-full" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-transparent pointer-events-none" />
              </AspectRatio>
              
              <div className="absolute bottom-6 left-6 right-6 bg-background/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-border flex items-center gap-4">
                <div className="bg-primary text-primary-foreground p-3 rounded-lg shrink-0">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h4 className="font-default font-bold text-foreground">Fast Dispatch</h4>
                  <p className="text-sm text-muted-foreground font-sans">From our North Fort Myers hub</p>
                </div>
              </div>
            </div>
            
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/10 rounded-lg blur-3xl pointer-events-none" />
          </div>

        </div>
      </div>
    </section>;
}